'use client';

import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import type { DrugInteraction, InteractionsSummary } from '../types';
import { getPairKey } from '../types';
import { type LegendSeverity, SeverityLegendLink } from './severity-legend';

interface InteractionsSummaryCardProps {
  summary: InteractionsSummary;
  drugCount: number;
  /** Top interactions to highlight */
  topInteractions?: DrugInteraction[];
  /** Called when a pair badge is clicked */
  onPairClick?: (interaction: DrugInteraction) => void;
  /** Shareable URL for copying */
  shareableUrl?: string;
  /** When true, renders only the title + stats line (no legend, no pair badges) */
  titleOnly?: boolean;
  /** Called when user clicks the copy-text button */
  onCopyText?: () => void;
  /** Whether text was just copied (shows confirmation) */
  textCopied?: boolean;
}

/**
 * Compact summary bar showing interaction counts at a glance
 */
export function InteractionsSummaryCard({
  summary,
  drugCount,
  topInteractions = [],
  onPairClick,
  shareableUrl: _shareableUrl,
  titleOnly = false,
  onCopyText: _onCopyText,
  textCopied: _textCopied = false,
}: InteractionsSummaryCardProps) {
  const { t } = useTranslation('interactions');
  const {
    totalPairs,
    interactionsFound,
    criticalCount,
    importantCount,
    moderateCount,
    minorCount,
  } = summary;
  // Get all pairs with interactions (exclude safe/green ones)
  const interactionPairs = topInteractions.filter((i) => i.hasInteraction);

  // Measure container width to decide layout dynamically.
  // When the PDF panel opens or the user resizes, the container width changes.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Column layout when the container is wide enough AND pair names aren't too long.
  // All 4 severity columns are always rendered with flex-1 (equal width), so empty
  // columns waste 1/4 of the space each. Subtract 18 chars per empty column from
  // the base budget of 70.
  const columnLengths = (
    ['critical', 'important', 'moderate', 'minor'] as const
  ).map((sev) => {
    const pairs = interactionPairs.filter((i) => i.severity === sev);
    if (pairs.length === 0) return 0;
    return Math.max(
      ...pairs.map(
        (i) =>
          i.pair.drug1.productName.length + i.pair.drug2.productName.length,
      ),
    );
  });
  const emptyColumns = columnLengths.filter((l) => l === 0).length;
  const totalLongestPairLength = columnLengths.reduce((a, b) => a + b, 0);
  const columnLimit = 70 - emptyColumns * 10;
  const useColumnLayout =
    containerWidth >= 640 && totalLongestPairLength <= columnLimit;

  return (
    <div ref={containerRef} className="@container">
      {/* Title + stats, legend link right-aligned on the stats line */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center text-3xl font-semibold tracking-tight text-zinc-700 dark:text-zinc-200">
            <span>{t('summary.title')}</span>
            {!titleOnly && (
              <span className="ml-1.5 translate-y-px text-2xl font-normal text-zinc-400 dark:text-zinc-500">
                ({interactionsFound})
              </span>
            )}
          </h1>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            <span className="font-light">
              {t('drugsCount', { count: drugCount })}
            </span>
            <span className="mx-1 font-light">→</span>
            <span className="font-light">
              {t('pairsChecked', { count: totalPairs })}
            </span>
          </p>
        </div>
        {!titleOnly && (
          <div className="flex shrink-0">
            <SeverityLegendLink />
          </div>
        )}
      </div>

      {!titleOnly && (
        <>
          <div className={cn('mt-3', useColumnLayout && 'flex')}>
            {(['critical', 'important', 'moderate', 'minor'] as const).map(
              (sev) => {
                const count = {
                  critical: criticalCount,
                  important: importantCount,
                  moderate: moderateCount,
                  minor: minorCount,
                }[sev];
                const pairs = interactionPairs.filter(
                  (i) => i.severity === sev,
                );
                return (
                  <SeverityRow
                    key={sev}
                    severity={sev}
                    count={count}
                    pairs={pairs}
                    onPairClick={onPairClick}
                    useColumnLayout={useColumnLayout}
                  />
                );
              },
            )}
          </div>
        </>
      )}
    </div>
  );
}

const severityRowColors = {
  critical: {
    bg: 'bg-red-100 dark:bg-red-900/40',
    border: 'border-red-200 dark:border-red-800',
    dot: 'bg-red-500',
    text: 'text-red-700 dark:text-red-400',
  },
  important: {
    bg: 'bg-orange-100 dark:bg-orange-900/40',
    border: 'border-orange-200 dark:border-orange-800',
    dot: 'bg-orange-500',
    text: 'text-orange-700 dark:text-orange-400',
  },
  moderate: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/40',
    border: 'border-yellow-200 dark:border-yellow-800',
    dot: 'bg-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-400',
  },
  minor: {
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    border: 'border-blue-200 dark:border-blue-800',
    dot: 'bg-blue-500',
    text: 'text-blue-700 dark:text-blue-400',
  },
};

function SeverityRow({
  severity,
  count,
  pairs,
  onPairClick,
  useColumnLayout,
}: {
  severity: LegendSeverity;
  count: number;
  pairs: DrugInteraction[];
  onPairClick?: (interaction: DrugInteraction) => void;
  useColumnLayout: boolean;
}) {
  const { t } = useTranslation('interactions');
  const colors = severityRowColors[severity];
  const label = t(`summary.severityCount.${severity}`, { count });
  const hasPairs = pairs.length > 0;

  const tab = (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 py-1 pr-3 pl-3 text-[11px] leading-normal',
        colors.bg,
        useColumnLayout ? 'rounded-r-md' : hasPairs && 'mb-6',
      )}
    >
      <span className={cn('h-2 w-2 rounded-full', colors.dot)} />
      <span className={cn('font-bold tabular-nums', colors.text)}>{count}</span>
      <h2 className={cn('font-medium', colors.text)}>{label}</h2>
    </div>
  );

  return (
    <div
      className={cn(
        'border-l-4',
        'border-t leading-[0]',
        useColumnLayout &&
          'flex flex-1 flex-col items-start pr-3 leading-normal',
        colors.border,
        hasPairs && cn('pb-4', useColumnLayout && 'pb-2'),
        count === 0 && !hasPairs && 'opacity-40',
      )}
    >
      {useColumnLayout ? (
        // Wide layout: short category description under the tab
        <div
          className={cn(
            'flex flex-col items-start self-start',
            hasPairs && 'mb-4',
          )}
        >
          {tab}
          <p className="mt-1 pl-3 text-[11px] font-light text-zinc-500 dark:text-zinc-400">
            {t(`severity.${severity}.short`)}
          </p>
        </div>
      ) : (
        tab
      )}
      {hasPairs && (
        <div
          className={cn(
            'flex flex-wrap gap-2 pl-4',
            useColumnLayout && 'flex-col pl-3',
          )}
        >
          {pairs.map((interaction) => (
            <PairBadge
              key={getPairKey(interaction.pair)}
              interaction={interaction}
              onClick={() => onPairClick?.(interaction)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PairBadgeProps {
  interaction: DrugInteraction;
  onClick?: () => void;
}

function PairBadge({ interaction, onClick }: PairBadgeProps) {
  const { severity, pair } = interaction;

  // Pastel style - light bg, light border, pastel shadow (matching legend badges)
  const colorConfig = {
    critical: {
      bg: 'bg-red-100 dark:bg-red-950',
      border: 'border-red-300 dark:border-red-700',
      text: 'text-red-700 dark:text-red-300',
      plus: 'text-red-400 dark:text-red-500',
      shadow:
        'shadow-[2px_2px_0_0_#fca5a5] hover:shadow-[1px_1px_0_0_#fca5a5] active:shadow-[1px_1px_0_0_#fca5a5] dark:shadow-[2px_2px_0_0_#991b1b] dark:hover:shadow-[1px_1px_0_0_#991b1b] dark:active:shadow-[1px_1px_0_0_#991b1b]',
    },
    important: {
      bg: 'bg-orange-100 dark:bg-orange-950',
      border: 'border-orange-300 dark:border-orange-700',
      text: 'text-orange-700 dark:text-orange-300',
      plus: 'text-orange-400 dark:text-orange-500',
      shadow:
        'shadow-[2px_2px_0_0_#fdba74] hover:shadow-[1px_1px_0_0_#fdba74] active:shadow-[1px_1px_0_0_#fdba74] dark:shadow-[2px_2px_0_0_#9a3412] dark:hover:shadow-[1px_1px_0_0_#9a3412] dark:active:shadow-[1px_1px_0_0_#9a3412]',
    },
    moderate: {
      bg: 'bg-yellow-100 dark:bg-yellow-950',
      border: 'border-yellow-300 dark:border-yellow-700',
      text: 'text-yellow-700 dark:text-yellow-300',
      plus: 'text-yellow-500 dark:text-yellow-500',
      shadow:
        'shadow-[2px_2px_0_0_#fde047] hover:shadow-[1px_1px_0_0_#fde047] active:shadow-[1px_1px_0_0_#fde047] dark:shadow-[2px_2px_0_0_#a16207] dark:hover:shadow-[1px_1px_0_0_#a16207] dark:active:shadow-[1px_1px_0_0_#a16207]',
    },
    minor: {
      bg: 'bg-blue-100 dark:bg-blue-950',
      border: 'border-blue-300 dark:border-blue-700',
      text: 'text-blue-700 dark:text-blue-300',
      plus: 'text-blue-400 dark:text-blue-500',
      shadow:
        'shadow-[2px_2px_0_0_#93c5fd] hover:shadow-[1px_1px_0_0_#93c5fd] active:shadow-[1px_1px_0_0_#93c5fd] dark:shadow-[2px_2px_0_0_#1e40af] dark:hover:shadow-[1px_1px_0_0_#1e40af] dark:active:shadow-[1px_1px_0_0_#1e40af]',
    },
    safe: {
      bg: 'bg-green-100 dark:bg-green-950',
      border: 'border-green-300 dark:border-green-700',
      text: 'text-green-700 dark:text-green-300',
      plus: 'text-green-400 dark:text-green-500',
      shadow:
        'shadow-[2px_2px_0_0_#86efac] hover:shadow-[1px_1px_0_0_#86efac] active:shadow-[1px_1px_0_0_#86efac] dark:shadow-[2px_2px_0_0_#166534] dark:hover:shadow-[1px_1px_0_0_#166534] dark:active:shadow-[1px_1px_0_0_#166534]',
    },
  };

  const colors = colorConfig[severity];

  return (
    <button
      type="button"
      onClick={onClick}
      title={`${pair.drug1.productName} + ${pair.drug2.productName}`}
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-md border-2 px-2.5 py-1 text-xs font-bold transition-all',
        'hover:translate-x-[1px] hover:translate-y-[1px]',
        'active:translate-x-[1px] active:translate-y-[1px]',
        colors.bg,
        colors.border,
        colors.text,
        colors.shadow,
      )}
    >
      <span className="max-w-[120px] truncate @[640px]:max-w-[200px]">
        {pair.drug1.productName}
      </span>
      <span className={cn('shrink-0', colors.plus)}>+</span>
      <span className="max-w-[120px] truncate @[640px]:max-w-[200px]">
        {pair.drug2.productName}
      </span>
    </button>
  );
}
